"use client";

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  SYSTEM_DARK_QUERY,
  THEME_CHANGE_EVENT,
  applyTheme,
  readTheme,
  resolveTheme,
  writeTheme,
  type Theme,
} from "@/lib/theme";

/**
 * The light/dark switch. Two states, not three: the site defaults to light and
 * this flips it, which is the whole of the preference model here — see
 * lib/theme.ts. A stored `system` is still resolved correctly if one arrives,
 * it just isn't something this control can produce.
 *
 * Which icon shows is decided by CSS from the `.dark` class, not by React
 * state. That matters for two reasons: the server has no idea which palette the
 * visitor uses, so any state-dependent markup would either mismatch on
 * hydration or flash the wrong icon; and the class is already correct before
 * the first paint because ThemeScript put it there. So both icons render and
 * the variant hides one. For the same reason the accessible name is fixed
 * rather than describing the current state.
 */
export function ThemeToggle({ className }: { className?: string }) {
  // A lazy initializer reading the same source as ThemeScript, which is what
  // Next's guide prescribes so state and DOM can never disagree. It answers
  // DEFAULT_THEME during SSR, where there is no storage to read; that the
  // client may compute something else is harmless here, because nothing this
  // component renders depends on it.
  const [theme, setThemeState] = useState<Theme>(readTheme);

  // Before paint rather than after: React's Strict Mode remount in development
  // resets <html> to the attributes it manages from JSX, throwing away the
  // class ThemeScript set. Re-applying here restores it. In production this is
  // a no-op on mount and the script's work stands.
  useLayoutEffect(() => {
    applyTheme(resolveTheme(readTheme()));
  }, []);

  // Another tab, or another control on this page, may have changed it.
  useEffect(() => {
    const sync = () => {
      const next = readTheme();
      setThemeState(next);
      applyTheme(resolveTheme(next));
    };
    window.addEventListener(THEME_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Only meaningful for a stored `system`, but the OS can flip at sunset while
  // the page is open and the class has to follow.
  useEffect(() => {
    if (theme !== "system") return;
    const query = window.matchMedia(SYSTEM_DARK_QUERY);
    const onChange = () => applyTheme(resolveTheme("system"));
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [theme]);

  const toggle = useCallback(() => {
    // The class is the source of truth the pre-paint script established, so ask
    // the document rather than trusting state that may not have synced yet.
    const next: Theme = document.documentElement.classList.contains("dark")
      ? "light"
      : "dark";
    setThemeState(next);
    writeTheme(next);
    applyTheme(next);
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Toggle dark mode"
      title="Toggle dark mode"
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-brand-ink",
        className
      )}
    >
      <Moon aria-hidden className="size-4.5 dark:hidden" />
      <Sun aria-hidden className="hidden size-4.5 dark:block" />
    </button>
  );
}
